import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, MapPin, Phone, Smartphone, ExternalLink, Building2 } from "lucide-react";

const empty = { facility_name: "", owner_name: "", location: "", map_url: "", business_phone: "", personal_phone: "", note: "" };

export default function Fields() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/field-owners").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (it) => {
    setForm(it);
    setEditId(it.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.facility_name || !form.owner_name) return toast.error("Tesis ve sahip adı zorunlu");
    try {
      if (editId) await api.put(`/field-owners/${editId}`, form);
      else await api.post("/field-owners", form);
      toast.success(editId ? "Saha sahibi güncellendi" : "Saha sahibi eklendi & yöneticilere e-posta bildirimi gönderildi");
      setOpen(false);
      load();

    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/field-owners/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Rehber</p>
          <h1 className="font-anton text-4xl sm:text-5xl">SAHA SAHİPLERİ</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-owner-button" onClick={openNew} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="w-4 h-4 mr-1" /> Sahip Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-anton text-2xl">{editId ? "SAHİBİ DÜZENLE" : "YENİ SAHA SAHİBİ"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tesis Adı</Label>
                <Input data-testid="owner-facility-input" value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} placeholder="Örn: Yıldız Halı Saha" />
              </div>
              <div className="space-y-2">
                <Label>Sahip Adı Soyadı</Label>
                <Input data-testid="owner-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} placeholder="Ad Soyad" />
              </div>
              <div className="space-y-2">
                <Label>Konum / Adres</Label>
                <Input data-testid="owner-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Mahalle, ilçe" />
              </div>
              <div className="space-y-2">
                <Label>Harita Linki (Google Maps)</Label>
                <Input data-testid="owner-map-input" value={form.map_url} onChange={(e) => setForm({ ...form, map_url: e.target.value })} placeholder="https://maps.google.com/..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tesis Telefonu</Label>
                  <Input data-testid="owner-business-phone-input" value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} placeholder="0212..." />
                </div>
                <div className="space-y-2">
                  <Label>Şahsi Telefon</Label>
                  <Input data-testid="owner-personal-phone-input" value={form.personal_phone} onChange={(e) => setForm({ ...form, personal_phone: e.target.value })} placeholder="0532..." />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Not</Label>
                <Input data-testid="owner-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="İsteğe bağlı" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-owner-button" onClick={save} className="bg-primary hover:bg-primary/90 text-white">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 && (
        <Card className="bg-card border-border p-10 text-center text-muted-foreground">
          Henüz saha sahibi eklenmedi.
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="owners-list">
        {items.map((o, i) => (
          <motion.div key={o.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="bg-card border-border p-5 h-full flex flex-col hover:border-primary/40 transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="w-11 h-11 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(o)} className="h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`delete-owner-${o.id}`} onClick={() => remove(o.id)} className="h-8 w-8 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <h3 className="font-anton text-xl mt-3">{o.facility_name}</h3>
              <p className="text-sm text-muted-foreground">{o.owner_name}</p>
              <div className="mt-4 space-y-2 text-sm flex-1">
                {o.location && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4 shrink-0 text-primary" /> <span className="truncate">{o.location}</span>
                  </div>
                )}
                {o.business_phone && (
                  <a href={`tel:${o.business_phone}`} className="flex items-center gap-2 hover:text-primary">
                    <Phone className="w-4 h-4 shrink-0 text-primary" /> {o.business_phone}
                  </a>
                )}
                {o.personal_phone && (
                  <a href={`tel:${o.personal_phone}`} className="flex items-center gap-2 hover:text-primary">
                    <Smartphone className="w-4 h-4 shrink-0 text-primary" /> {o.personal_phone}
                  </a>
                )}
                {o.note && <p className="text-xs text-muted-foreground pt-1">{o.note}</p>}
              </div>
              {o.map_url && (
                <a href={o.map_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm text-primary hover:underline">
                  Haritada Aç <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
